<script setup lang="ts">
import LastCommentPreview from '@organisms/offer/selection-items/components/comments/LastCommentPreview.vue';
import type { SelectionItem } from '@models/selectionItem.ts';
import { useSelectionItemCommentsStore } from '@organisms/offer/selection-items/stores/useSelectionItemCommentsStore';
import { Direction } from '@enums/direction.ts';
import { transchoice } from '@elements/translation';
import Tooltip from '@components/Tooltip.vue';
import Typography from '@components/Typography.vue';
import IconChatText from '@icons/ph_chat-text.svg';
import Icon from '@components/Icon.vue';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import { computed } from 'vue';
import { SelectionItemProductTour } from '@enums/productTour.ts';
import useSelectionItemStore from '@organisms/offer/selection-items/stores/useSelectionItemStore';
import { storeToRefs } from 'pinia';

const props = defineProps<{ selectionItem: SelectionItem }>();

const selectionItemStore = useSelectionItemStore();
const { firstItemId } = storeToRefs(selectionItemStore);

const { openCommentsModal } = useSelectionItemCommentsStore();

const { isMobileOrTablet } = useScreensizeStore();

const commentsCounterId = computed<SelectionItemProductTour | undefined>(
  () => (props.selectionItem.id === firstItemId.value ? SelectionItemProductTour.TOUR_ADD_COMMENT : undefined),
);
</script>

<template>
  <div class="selection-item-comments">
    <div class="selection-item-comments__counter">
      <Typography
        v-if="isMobileOrTablet || !selectionItem.search_comment.counter"
        :id="commentsCounterId"
        class="selection-item-comments__text"
        variant="caption"
        as="span"
        @click="openCommentsModal(selectionItem)"
      >
        <Icon :svg="IconChatText" width="16" height="16" />

        {{ transchoice('selection_item.comment.counter', { count: selectionItem.search_comment.counter }) }}
      </Typography>

      <Tooltip
        v-else
        :direction="Direction.UP"
        panel-class="selection-item-comments__tooltip-panel"
      >
        <template #trigger>
          <Typography
            :id="commentsCounterId"
            class="selection-item-comments__text"
            variant="caption"
            as="span"
            @click="openCommentsModal(selectionItem)"
          >
            <Icon :svg="IconChatText" width="16" height="16" />

            {{ transchoice('selection_item.comment.counter', { count: selectionItem.search_comment.counter }) }}
          </Typography>
        </template>

        <template #panel>
          <Typography
            v-for="authorFullname in selectionItem.search_comment.authors"
            :key="authorFullname"
            variant="body-small"
          >
            {{ authorFullname }}
          </Typography>
        </template>
      </Tooltip>
    </div>

    <LastCommentPreview
      :last-comment="selectionItem.search_comment.last_comment"
      @click="openCommentsModal(selectionItem)"
    />
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.selection-item-comments {
  display: flex;
  flex-direction: column;
  gap: rem(4);

  &__counter {
    display: flex;
  }

  &__text {
    border-radius: rem(8);
    padding: rem(4);
    display: flex;
    align-items: center;
    gap: rem(4);
    cursor: pointer;

    &:hover {
      background-color: $colorBlueLighter;
    }
  }
}
</style>
